import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Check, X, CheckCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { relativeTime } from "@/lib/utils";
import { DocStatusPill, docFileUrl } from "@/components/VerificationDocList";

const STATUS_FILTERS = ["pending", "approved", "rejected", "all"];
const SCOPE_FILTERS = [{ k: "all", label: "All types" }, { k: "company", label: "Company" }, { k: "development", label: "Development" }];

function Chip({ active, onClick, children, testId }) {
  return <button data-testid={testId} onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-semibold border capitalize transition-colors ${active ? "bg-propera text-white border-propera" : "bg-white border-neutral-200 hover:border-neutral-300"}`}>{children}</button>;
}

function ReviewActions({ doc, onDone }) {
  const [busy, setBusy] = useState(false);
  async function review(status) {
    let note = null;
    if (status === "rejected") {
      note = window.prompt("Reason for rejection (shown to the developer):");
      if (!note || !note.trim()) return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/verification/documents/${doc.id}`, { status, note });
      toast.success(`Document ${status}${data.developer_verified ? " · developer now verified" : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }
  return (
    <div className="flex items-center gap-1.5">
      <a href={docFileUrl(doc.id)} target="_blank" rel="noopener noreferrer" data-testid={`admin-doc-view-${doc.id}`} className="p-2 rounded-full hover:bg-neutral-100 text-neutral-600" aria-label="View document"><Eye size={15} /></a>
      {doc.status !== "approved" && (
        <button disabled={busy} data-testid={`admin-doc-approve-${doc.id}`} onClick={() => review("approved")} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-60"><Check size={12} /> Approve</button>
      )}
      {doc.status !== "rejected" && (
        <button disabled={busy} data-testid={`admin-doc-reject-${doc.id}`} onClick={() => review("rejected")} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-60"><X size={12} /> Reject</button>
      )}
    </div>
  );
}

function BulkBar({ count, busy, onApprove, onReject, onClear }) {
  if (!count) return null;
  return (
    <div className="sticky top-20 z-20 mt-4 flex items-center gap-3 flex-wrap rounded-2xl bg-neutral-900 text-white px-4 py-3 shadow-lg animate-fade-up" data-testid="bulk-action-bar">
      <span className="text-sm font-semibold" data-testid="bulk-selected-count">{count} selected</span>
      <button disabled={busy} data-testid="bulk-approve" onClick={onApprove} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-white transition-colors disabled:opacity-60"><CheckCheck size={13} /> Approve selected</button>
      <button disabled={busy} data-testid="bulk-reject" onClick={onReject} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-red-500 hover:bg-red-400 text-white transition-colors disabled:opacity-60"><X size={13} /> Reject selected</button>
      <button data-testid="bulk-clear" onClick={onClear} className="ml-auto text-xs text-white/70 hover:text-white">Clear</button>
    </div>
  );
}

function RejectDialog({ count, open, busy, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (open) setNote(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl" data-testid="bulk-reject-dialog">
        <DialogHeader>
          <DialogTitle>Reject {count} document{count === 1 ? "" : "s"}?</DialogTitle>
          <DialogDescription>This reason is shared with every developer affected.</DialogDescription>
        </DialogHeader>
        <textarea data-testid="bulk-reject-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for rejection (required)" className="w-full px-3 py-2 rounded-xl border border-neutral-200 focus:border-propera outline-none text-sm" />
        <DialogFooter className="gap-2">
          <button data-testid="bulk-reject-cancel" onClick={onClose} className="px-4 py-2 rounded-full border border-neutral-200 text-sm font-semibold">Cancel</button>
          <button data-testid="bulk-reject-confirm" disabled={busy || !note.trim()} onClick={() => onConfirm(note.trim())} className="px-4 py-2 rounded-full bg-red-600 text-white text-sm font-semibold disabled:opacity-50">{busy ? "Rejecting…" : "Reject documents"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminVerificationTab() {
  const [status, setStatus] = useState("pending");
  const [scope, setScope] = useState("all");
  const [data, setData] = useState({ items: [], counts: {} });
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  async function load() {
    const { data } = await api.get(`/admin/verification/queue?status=${status}&scope=${scope}`);
    setData(data);
    setSelected((sel) => sel.filter((id) => data.items.some((d) => d.id === id && d.status === "pending")));
  }
  useEffect(() => { load(); }, [status, scope]);

  const pendingIds = data.items.filter((d) => d.status === "pending").map((d) => d.id);
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.includes(id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  async function bulk(statusTo, note = null) {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/verification/documents/bulk", { ids: selected, status: statusTo, note });
      const verb = statusTo === "approved" ? "approved" : "rejected";
      toast.success(`${r.updated.length} document${r.updated.length === 1 ? "" : "s"} ${verb}${r.developers_verified.length ? ` · ${r.developers_verified.length} developer(s) now verified` : ""}`);
      if (r.failed.length) toast.error(`${r.failed.length} could not be updated: ${r.failed.map((f) => f.reason).join("; ")}`);
      setSelected([]); setRejectOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="admin-verification-tab">
      <div className="flex flex-wrap gap-2 items-center">
        {STATUS_FILTERS.map((s) => (
          <Chip key={s} active={status === s} onClick={() => setStatus(s)} testId={`admin-verif-status-${s}`}>
            {s}{data.counts?.[s] != null ? ` (${data.counts[s]})` : ""}
          </Chip>
        ))}
        <span className="w-px h-5 bg-neutral-200 mx-1" />
        {SCOPE_FILTERS.map((s) => <Chip key={s.k} active={scope === s.k} onClick={() => setScope(s.k)} testId={`admin-verif-scope-${s.k}`}>{s.label}</Chip>)}
      </div>
      <BulkBar count={selected.length} busy={busy} onApprove={() => bulk("approved")} onReject={() => setRejectOpen(true)} onClear={() => setSelected([])} />
      <RejectDialog count={selected.length} open={rejectOpen} busy={busy} onClose={() => setRejectOpen(false)} onConfirm={(n) => bulk("rejected", n)} />
      <div className="soft-card overflow-x-auto mt-4">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase tracking-wider">
            <tr><th className="pl-5 py-3 w-8"><input type="checkbox" data-testid="bulk-select-all" disabled={!pendingIds.length} checked={allSelected} onChange={() => setSelected(allSelected ? [] : pendingIds)} className="w-4 h-4 accent-[#0E8C5A] cursor-pointer" aria-label="Select all pending" /></th><th className="text-left px-5 py-3">Document</th><th className="text-left px-5 py-3">Belongs to</th><th className="text-left px-5 py-3">Uploaded by</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {data.items.map((d) => (
              <tr key={d.id} className={`border-t border-neutral-100 align-top transition-colors ${selected.includes(d.id) ? "bg-propera-light/40" : ""}`} data-testid={`admin-doc-${d.id}`}>
                <td className="pl-5 py-3.5">{d.status === "pending" && <input type="checkbox" data-testid={`bulk-select-${d.id}`} checked={selected.includes(d.id)} onChange={() => toggle(d.id)} className="w-4 h-4 accent-[#0E8C5A] cursor-pointer" aria-label="Select document" />}</td>
                <td className="px-5 py-3"><p className="font-semibold">{d.doc_label}</p><p className="text-xs text-neutral-500 truncate max-w-[220px]">{d.original_filename} · {relativeTime(d.created_at)}</p></td>
                <td className="px-5 py-3">
                  {d.scope === "company"
                    ? <span className="text-xs"><span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-semibold">Company</span> <span className="block mt-1 text-neutral-600">{d.owner?.company_name || d.owner?.name} · {d.owner?.company_country || d.owner?.country || "—"}</span></span>
                    : <span className="text-xs"><span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-semibold">Development</span> <Link to={`/development/${d.development_id}`} className="block mt-1 text-neutral-600 hover:text-propera">{d.development?.name || "—"}</Link></span>}
                </td>
                <td className="px-5 py-3"><Link to={`/u/${d.owner_id}`} className="hover:text-propera">{d.owner?.name}</Link><p className="text-xs text-neutral-500">{d.owner?.email}</p></td>
                <td className="px-5 py-3"><DocStatusPill status={d.status} testId={`admin-doc-status-${d.id}`} />{d.review_note && <p className="text-[11px] text-neutral-500 mt-1 max-w-[180px]">{d.review_note}</p>}</td>
                <td className="px-5 py-3"><ReviewActions doc={d} onDone={load} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.items.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center" data-testid="admin-verif-empty">No documents in this queue.</p>}
      </div>
    </div>
  );
}
